import type { HTMLAttributes, ReactNode } from 'react';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { Heading } from '../heading';
import css from './slide-panel.module.scss';

type SlidePanelProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Keep the body scrollable but hide its scrollbar. Opt-in, so no existing
   * panel changes appearance; wheel/trackpad/keyboard scrolling is untouched.
   */
  hideScrollbar?: boolean;
  children: ReactNode;
  onClose: () => void;
  open: boolean;
  title: string;
  width?: number;
};

export const SlidePanel = ({
  children,
  className,
  hideScrollbar = false,
  onClose,
  open,
  title,
  width = 400,
  ...restProps
}: SlidePanelProps) => {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={css.overlay}>
      <button
        className={css.backdrop}
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />
      <div
        className={joinCss(css.panel, className)}
        style={{ '--slide-panel-width': `${width}px` } as React.CSSProperties}
        {...restProps}
      >
        <div className={css.header}>
          <Heading as="h2" className={css.title} size="sm">
            {title}
          </Heading>
          <button className={css.closeBtn} onClick={onClose} type="button">
            <X className={css.closeIcon} />
          </button>
        </div>
        <div
          className={joinCss(css.body, hideScrollbar && css.bodyHideScrollbar)}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
